import { useEffect, useState } from 'react'
import { fetchColorfulAiPlayers } from '../api/aiPlayers'
import type { AiPlayerRow } from '../../../lib/database.types'
import { ColorfulPassPlayGameScreen } from './ColorfulPassPlayGameScreen'
import { ColorfulResultsScreen } from '../screens/ColorfulResultsScreen'
import { PassPlaySetupScreen } from '../../passplay/PassPlaySetupScreen'
import type { PassPlayPlayer } from '../../passplay/types'
import type { ColorfulPlayer, ColorfulState } from '../engine/types'

interface Props {
  onExit: () => void
}

/**
 * Colorful — Phone Pass Play. The existing ColorfulGameScreen + engine
 * already handles a hot-seat flow with mixed humans + AIs; the only
 * thing missing was a setup screen that lets users add multiple named
 * humans (the standard ColorfulLobbyScreen is solo-vs-AI). This
 * wrapper feeds the shared PassPlay setup into the existing engine.
 */
export function ColorfulPassPlayApp({ onExit }: Props) {
  const [aiPool, setAiPool] = useState<AiPlayerRow[] | null>(null)
  const [players, setPlayers] = useState<ColorfulPlayer[] | null>(null)
  const [final, setFinal] = useState<ColorfulState | null>(null)

  useEffect(() => {
    fetchColorfulAiPlayers(50).then(setAiPool).catch(() => setAiPool([]))
  }, [])

  if (final) {
    return (
      <ColorfulResultsScreen
        state={final}
        onPlayAgain={() => { setFinal(null); setPlayers(null) }}
        onExit={onExit}
      />
    )
  }

  if (players) {
    return (
      <ColorfulPassPlayGameScreen
        players={players}
        onExit={onExit}
        onFinished={setFinal}
      />
    )
  }

  if (!aiPool) {
    return (
      <div className="screen items-center justify-center">
        <p className="text-brand-muted">Loading…</p>
      </div>
    )
  }

  return (
    <PassPlaySetupScreen
      title="Colorful"
      primaryButtonClass="btn-colorful"
      aiPool={aiPool.map(a => ({
        id: a.id, name: a.name, avatarEmoji: a.avatar_emoji,
      }))}
      minPlayers={2}
      maxPlayers={6}
      onStart={(passplayPlayers) => {
        setPlayers(passplayPlayers.map(p => mapToColorfulPlayer(p, aiPool)))
      }}
      onBack={onExit}
    />
  )
}

function mapToColorfulPlayer(p: PassPlayPlayer, aiPool: AiPlayerRow[]): ColorfulPlayer {
  if (p.kind === 'ai') {
    const persona = aiPool.find(a => a.id === p.aiPlayerId)
    return {
      id: p.id,
      name: p.name,
      isAi: true,
      avatarEmoji: p.avatarEmoji ?? '🤖',
      personaDescription: persona?.persona ?? undefined,
    }
  }
  return { id: p.id, name: p.name, isAi: false }
}
